import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Divider, Stack } from "@mui/material";
import Scrollbars from "react-custom-scrollbars-2";
import ProfileWrapper from "./components/ProfileWrapper";
import StickyFooter from "./components/Footer";
import CancelFormModal from "../../components/ui/Modals/SettingsModals/CancelFormModal";
import CommonModalPopUp from "../../components/ui/Modals/ModalPopupCommon";
import authFunctions from "../../services/userApiServices/authFunctions";
import CachedImage from "../../components/common/CachedImage";
import ProfileLeftSection from "./components/LayoutSections/LeftSection";
import ProfileRightSection from "./components/LayoutSections/RightSection";

import { ImagePath } from "../../assets/ImagePath";
import { showToast } from "../../components/ui/toaster";
import { setUserProfileImage, updateUserProfile } from "../../state/actions/user";
import { profileApiServices } from "../../services/userApiServices/profile";
import { isUserOnline } from "../../services/storage/LocalStorage";
import { setShowDefaultHomepageBot } from "../../state/actions/bot";
import { useNavigate } from "react-router-dom";
import { setHeaderTitle } from "../../state/actions/layout";
import { setBotWaitLoader } from "../../state/actions/loader";



const MyProfileContainer = () => {
    const dispatch = useDispatch();
    const [mentor, setMentor] = useState("");
    const [aboutMe, setAboutMe] = useState("");
    const [emailAddress, setEmailAddress] = useState("");
    const [media, setMedia] = useState([]);
    const [satellite, setSatellite] = useState([]);
    const [land, setLand] = useState([]);
    const [mobile, setMobile] = useState([]);
    const [nationality, setNationality] = useState("");
    const [userCompanyName, setUserCompanyName] = useState("");
    const [address, setAddress] = useState({});
    const [userTimezone, setUserTimezone] = useState("");
    const [isModalSaving, setIsModalSaving] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [userData, setUserData] = useState({});
    const [trackUnstagedChanges, setTrackUnstagedChanges] = useState({});
    const [unsavedModal, setUnsavedModal] = useState({ show: false, message: "" });
    const [userProfileImage, setProfileImage] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [resetSectionFields, setResetSectionFields] = useState(false);

    const user = useSelector((state) => state.user?.user || null);
    const profileImage = useSelector((state) => state.user?.profileImage || null);
    const navigate = useNavigate();

    useEffect(() => {
        if (profileImage) {
            setProfileImage(profileImage);
        }
        else if (user && user.userId) {
            fetchProfilePic();
        }
    }, [profileImage]);


    const fetchProfilePic = async () => {
        try {
            setUploading(true);
            const rawUrl = authFunctions.getUserProfilePic(user.userId);

            // Add timestamp to bust cache
            const timestampedUrl = `${rawUrl}?t=${Date.now()}`;

            const response = await fetch(timestampedUrl);
            if (response.ok) {
                dispatch(setUserProfileImage(timestampedUrl));
                setProfileImage(timestampedUrl);
            }
            setUploading(false);
        } catch (err) {
            console.error("Error fetching profile picture:", err);
            setUploading(false);
        }
    };


    useEffect(() => {
        window.document.title = "My Profile";
        if (user) {
            setMentor(user.mentor || false);
            setAboutMe(user.aboutMe || "");
            setEmailAddress(user.emailAddress || "");
            setMedia(user.media || []);
            setSatellite(user.multiPhoneNumbers?.satellite || []);
            setLand(user.multiPhoneNumbers?.land || []);
            setMobile(user.multiPhoneNumbers?.mobile || []);
            setNationality(user.nationality || "");
            setUserCompanyName(user.userCompanyName || "");
            setAddress(
                user.address || {
                    addressLine1: "",
                    addressLine2: "",
                    city: "",
                    state: "",
                    country: "",
                    postCode: "",
                }
            );
            setUserTimezone(user.userTimezone || "");
        }
    }, [user]);


    const transformStagedDataToPayload = (stagedData, componentType) => {
        switch (componentType) {
            case "aboutMe":
                setAboutMe(stagedData[0].value);
                break;
            case "emailAddress":
                setEmailAddress(stagedData[0].value);
                break;
            case "media":
                const mediaFormats = stagedData.map(({ name, value }) => ({
                    socialNetwork: name,
                    socialURL: value,
                }));
                setMedia(mediaFormats);
                break;
            case "satellite":
                const satNumbers = stagedData.map(({ value }) => value);
                setSatellite(satNumbers);
                break;
            case "land":
                const landLines = stagedData.map(({ value }) => value);
                setLand(landLines);
                break;
            case "mobile":
                const mobiles = stagedData.map(({ value }) => value);
                setMobile(mobiles);
                break;
            case "form":
                stagedData.forEach((item) => {
                    switch (item.id) {
                        case "nationality":
                            const nationalityData = item.options.find(
                                (i) => i.code === item.value
                            );
                            setNationality(nationalityData?.nation || "");
                            break;
                        case "userCompanyName":
                            setUserCompanyName(item.value || "");
                            break;
                        case "userTimezone":
                            setUserTimezone(item.value || "");
                            break;
                        case "addressLine1":
                        case "addressLine2":
                        case "city":
                        case "state":
                        case "postCode":
                            setAddress((prev) => ({ ...prev, [item.id]: item.value }));
                            break;
                        case "country":
                            const countryData = item.options.find((i) => i.code === item.value);
                            setAddress((prev) => ({ ...prev, [item.id]: countryData?.name || '' }));
                            break;
                        default:
                    }
                });
                break;
            case "mentor":
                setIsSaveDisabled(false);
                setMentor(stagedData[0].value);
                break;
            default:
        }
    };

    const handleCancel = () => {
        if (!!Object.keys(trackUnstagedChanges).find((x) => (trackUnstagedChanges[x] === true)) || !isSaveDisabled) {
            return setUnsavedModal({
                show: true,
                message: "Your changes are not saved. Do you still want to leave?",
                callBack: () => handleHomeNaviagte()
            });
        }
        else {
            handleHomeNaviagte()
        }
    };

    const handleHomeNaviagte = () => {
        dispatch(setShowDefaultHomepageBot());
        dispatch(setHeaderTitle("Home"));
        dispatch(setBotWaitLoader(true));
        navigate("/app/chats");
    };

    const saveProfileAPI = async (userRoleData) => {
        setIsModalSaving(true)
        let profilePayload = {};
        if (userRoleData && Object.keys(userRoleData).length > 0) {
            profilePayload = {
                emailAddress: emailAddress,
                rank: userRoleData.rank,
                rankLevel1: userRoleData.rankLevel1,
                rankLevel2: userRoleData.rankLevel2,
                rankLevel3: userRoleData.rankLevel3,
                role: userRoleData.role,
                userName: userRoleData.userName,
                sailingStatus: userRoleData.sailingStatus,
                shipName: userRoleData.shipName,
                shipIMO: userRoleData.shipIMO,
            };
        } else {
            setIsSaving(true);
            profilePayload = {
                aboutMe: aboutMe,
                emailAddress: emailAddress,
                media: media,
                multiPhoneNumbers: { satellite, land, mobile },
                nationality: nationality,
                userCompanyName: userCompanyName,
                address: address,
                userTimezone: userTimezone,
                mentor: mentor,
                userName: user.userName,
                visible: user.visible,
                searchable: user.searchable,
            };
        }

        profileApiServices.updateUserProfile(profilePayload).then(() => {
            dispatch(updateUserProfile(profilePayload));
            // dispatch(unsavedForm(false));
            setIsSaving(false);
            setIsSaveDisabled(true);
            showToast('success', "Profile has been updated");
        }).catch((err) => {
            console.error("Save failed", err);
            showToast("error", "Something went wrong. Please try again.");
        })
            .finally(() => {
                setIsModalSaving(false)
            });
    };


    const saveProfile = (userRoleData) => {
        if (!!Object.keys(trackUnstagedChanges).find((x) => (trackUnstagedChanges[x] === true))) {
            return setUnsavedModal({
                show: true,
                message: "Warning!: There are unsaved changes. Do you want to continue anyway?.",
                callBack: () => saveProfileAPI(userRoleData)
            });
        }
        saveProfileAPI(userRoleData);
        handleHomeNaviagte()
    };

    const handleStagedData = (stagedData, componentType) => {
        transformStagedDataToPayload(stagedData, componentType);
    };


    if (!isUserOnline()) {
        return (
            <div className="bg-white rounded-[25px] h-full w-[94%] mx-[42px] my-[10px_0_20px_0] flex items-center justify-center">
                <div className="flex flex-col items-center text-center p-5">
                    <CachedImage
                        imgKey="sad-cloud-offline"
                        image={ImagePath.sadCloud}
                        style={{ height: '65px', width: '65px' }}
                        alt="Offline"
                    />
                    <p className="font-semibold mt-3 mb-0">Profile is not available offline.</p>
                    <p>Please connect and try again.</p>
                </div>
            </div>
        );
    };

    return (
        <>
            <ProfileWrapper>
                <Stack
                    sx={{ width: "100%", padding: "2rem" }}
                    flexDirection={"row"}
                    alignItems={"flex-start"}
                    justifyContent={"center"}
                    gap={4}
                >
                    <Scrollbars autoHide style={{ height: "calc(100vh - 240px)", width: "50%" }} >
                        <ProfileLeftSection
                            user={user}
                            save={saveProfile}
                            userData={userData}
                            uploading={uploading}
                            setUserData={setUserData}
                            setUploading={setUploading}
                            profileImage={userProfileImage}
                            loadProfilePic={fetchProfilePic}
                            handleStagedData={handleStagedData}
                            setIsSaveDisabled={setIsSaveDisabled}
                            resetSectionFields={resetSectionFields}
                            setTrackUnstagedChanges={setTrackUnstagedChanges}
                            isModalSaving={isModalSaving}
                            isDisabled={isSaveDisabled}
                        />
                    </Scrollbars>
                    <Divider orientation="vertical" flexItem />
                    <Scrollbars autoHide style={{ height: "calc(100vh - 240px)", width: "50%" }} >
                        {user &&
                            <ProfileRightSection
                                user={user}
                                handleStagedData={handleStagedData}
                                setIsSaveDisabled={setIsSaveDisabled}
                                setTrackUnstagedChanges={setTrackUnstagedChanges}
                            />}
                    </Scrollbars>
                </Stack>
                <StickyFooter
                    handleCancel={handleCancel}
                    saveProfile={() => saveProfile(userData)}
                    isSaving={isSaving}
                    isDisabled={isSaveDisabled}
                />
                {unsavedModal?.show && (
                    <CommonModalPopUp size="sm" noHeader hideCrosIcon>
                        <CancelFormModal
                            onCloseFn={() => setUnsavedModal({ show: false })}
                            onAccept={() => {
                                setResetSectionFields((pre) => !pre);
                                setUnsavedModal({ show: false });
                                handleHomeNaviagte();
                            }}
                            message={unsavedModal?.message}
                        />
                    </CommonModalPopUp>)}
            </ProfileWrapper>
        </>
    );
};

export default MyProfileContainer;
